/**
 * /earthworks/inventory/:id — one inventory item (EW-9), and
 * /earthworks/inventory/new — a new one. Full-width panels, no modals except
 * the delete confirmation.
 *
 * - Details, then on hand by location, then the stock actions: Stock in (with
 *   cost, supplier, reference), Adjust (a reason is required — the API says so
 *   if it is missing) and Transfer between locations. Each is an inline panel.
 * - The history is every move, newest first, as the API returns it; a daily-log
 *   use names its job and day.
 * - Low and below-zero stock come back as warnings after a move and show in
 *   amber — the move was saved.
 * - Prices (cost, sell, stock value, a receipt's cost) only for the roles that
 *   read the rate card. Stock moves: admin, office, supervisor, dispatcher.
 *   Edit and delete: admin and office.
 */
import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftRight, PackagePlus, Pencil, SlidersHorizontal, Trash2, Warehouse } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeaderBand } from '../../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../../components/ui/LoadFailureNotice';
import { useAuth } from '../../../contexts/AuthContext';
import { useEwMeta } from '../../../hooks/useEarthworks';
import {
  useAdjustEwMaterial,
  useCreateEwMaterial,
  useDeleteEwMaterial,
  useEwMaterial,
  useEwMaterialLocations,
  useEwMaterials,
  useReceiveEwMaterial,
  useTransferEwMaterial,
  useUpdateEwMaterial,
} from '../../../hooks/useEwInventory';
import { queryFailureError, queryLoadFailure } from '../../../lib/load-failure';
import { blankToNull, canAdminEw, canDispatchEw, canReadEwRates, catalogLabel, ewDay, ewRefusal, fmtMoney } from '../../../lib/earthworks';
import type { EwMaterialDetail, EwMaterialLocation, EwMaterialMove, EwMoveSaved } from '../../../types/earthworks';
import { DH_BAND_SECONDARY } from '../../downhole/DownholeBits';
import { useBackToList, useOpenRecord } from '../../downhole/recordPage';
import {
  isNotFound,
  EW_DATE_INPUT,
  EW_EYEBROW,
  EW_INPUT,
  EW_PRIMARY,
  EW_SECONDARY,
  EwBadge,
  EwDialog,
  EwEmpty,
  EwField,
  EwFormError,
  EwLoading,
  EwPanel,
  EwRegister,
  EwSpec,
  type EwColumn,
} from '../EwBits';
import { EwMaterialForm } from './MaterialForm';
import { EW_STOCK_SKIN, EwStockWarnings, ewQty, ewSignedQty, stockState } from './inventoryBits';

const QTY = /^\d+(\.\d{1,3})?$/;
const SIGNED_QTY = /^-?\d+(\.\d{1,3})?$/;
const AMOUNT = /^\d+(\.\d{1,2})?$/;

export function EwNewMaterialScreen() {
  const navigate = useNavigate();
  const back = useBackToList('/earthworks/inventory');
  const { user } = useAuth();
  const meta = useEwMeta().data?.meta ?? null;
  // The catalog (categories, units, rate materials) comes with the list read.
  const options = useEwMaterials().data?.options;
  const locations = useEwMaterialLocations().data?.data ?? [];
  const create = useCreateEwMaterial();
  const [error, setError] = useState<string | null>(null);
  return (
    <div data-testid="ew-material-new-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand eyebrow={`${EW_EYEBROW} · Inventory`} title="New item" onBack={back} backLabel="Back" />
      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {!canAdminEw(user?.role) ? (
            <EwPanel eyebrow="Inventory" title="New item">
              <EwEmpty>Only the office adds inventory items.</EwEmpty>
            </EwPanel>
          ) : (
            <EwPanel eyebrow="Inventory" title="New item" testId="ew-material-new">
              <EwMaterialForm
                material={null}
                options={options}
                locations={locations}
                currency={meta?.currency ?? null}
                unitLocked={false}
                pending={create.isPending}
                error={error}
                submitLabel="Add item"
                onCancel={back}
                onSubmit={(body) =>
                  create.mutate(body, {
                    onSuccess: (r) => {
                      toast.success('Item added');
                      navigate(r.data ? `/earthworks/inventory/${r.data.id}` : '/earthworks/inventory', { replace: true });
                    },
                    onError: (err) => setError(ewRefusal(err, 'The item could not be added.')),
                  })
                }
              />
            </EwPanel>
          )}
        </div>
      </section>
    </div>
  );
}

type Action = 'receive' | 'adjust' | 'transfer' | null;

function MoveForm({
  action,
  material,
  locations,
  today,
  canSeePrices,
  onDone,
  onCancel,
}: {
  action: Exclude<Action, null>;
  material: EwMaterialDetail;
  locations: EwMaterialLocation[];
  today: string | null;
  canSeePrices: boolean;
  onDone: (res: EwMoveSaved) => void;
  onCancel: () => void;
}) {
  const receive = useReceiveEwMaterial();
  const adjust = useAdjustEwMaterial();
  const transfer = useTransferEwMaterial();
  const pending = receive.isPending || adjust.isPending || transfer.isPending;
  const unit = material.unitLabel || material.unit;
  const [f, setF] = useState({
    quantity: '',
    unitCost: material.costPerUnit != null ? String(material.costPerUnit) : '',
    locationId: material.locationId ?? '',
    toLocationId: '',
    supplier: '',
    reference: '',
    reason: '',
    day: today ?? '',
    notes: '',
  });
  const [error, setError] = useState<string | null>(null);
  const set = (patch: Partial<typeof f>) => setF((cur) => ({ ...cur, ...patch }));
  const fail = (err: unknown) => setError(ewRefusal(err, 'The stock move could not be saved.'));

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const q = f.quantity.trim();
    if (action === 'adjust') {
      if (!SIGNED_QTY.test(q) || Number(q) === 0) return setError(`Enter the change in ${unit}: + to add, − to take away (not 0).`);
    } else if (!QTY.test(q) || Number(q) <= 0) {
      return setError(`Enter a quantity in ${unit} above 0.`);
    }
    if (action === 'receive' && f.unitCost.trim() && !AMOUNT.test(f.unitCost.trim())) return setError('The cost per unit must be an amount, like 28 or 28.50.');
    const handlers = { onSuccess: onDone, onError: fail };
    if (action === 'receive') {
      receive.mutate({ id: material.id, body: {
        quantity: q, unit_cost: canSeePrices ? blankToNull(f.unitCost) : undefined, location_id: f.locationId || null,
        supplier: blankToNull(f.supplier), reference: blankToNull(f.reference), received_on: f.day || null, notes: blankToNull(f.notes),
      } }, handlers);
    } else if (action === 'adjust') {
      // A missing reason is the API's to refuse (reason_required) — said as its sentence.
      adjust.mutate({ id: material.id, body: {
        quantity: q, reason: f.reason.trim(), location_id: f.locationId || null, adjusted_on: f.day || null, notes: blankToNull(f.notes),
      } }, handlers);
    } else {
      transfer.mutate({ id: material.id, body: {
        quantity: q, from_location_id: f.locationId || null, to_location_id: f.toLocationId || null, moved_on: f.day || null, notes: blankToNull(f.notes),
      } }, handlers);
    }
  };

  const title = action === 'receive' ? 'Stock in' : action === 'adjust' ? 'Adjust stock' : 'Transfer between locations';
  const locSelect = (label: string, value: string, onChange: (v: string) => void, blank: string) => (
    <EwField label={label}>
      <select className={EW_INPUT} aria-label={label} value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">{blank}</option>
        {locations.map((l) => (
          <option key={l.id} value={l.id}>{l.name}</option>
        ))}
      </select>
    </EwField>
  );

  return (
    <EwPanel eyebrow="Stock" title={title} testId={`ew-material-${action}`}>
      <form onSubmit={submit} className="space-y-4 px-4 py-4">
        <EwFormError message={error} />
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <EwField label={action === 'adjust' ? `Change (${unit}), + or −` : `Quantity (${unit})`}>
            <input className={EW_INPUT} inputMode="decimal" aria-label="Quantity" value={f.quantity} onChange={(e) => set({ quantity: e.target.value })} />
          </EwField>
          {action === 'transfer'
            ? (<>{locSelect('From', f.locationId, (v) => set({ locationId: v }), 'No location')}{locSelect('To', f.toLocationId, (v) => set({ toLocationId: v }), 'No location')}</>)
            : locSelect('Location', f.locationId, (v) => set({ locationId: v }), 'No location')}
          <EwField label={action === 'receive' ? 'Received on' : action === 'adjust' ? 'Counted on' : 'Moved on'}>
            <input type="date" className={EW_DATE_INPUT} aria-label="Day" value={f.day} max={today ?? undefined} onChange={(e) => set({ day: e.target.value })} />
          </EwField>
          {action === 'receive' && (
            <>
              {canSeePrices && (
                <EwField label={`Cost per unit${material.currency ? ` (${material.currency})` : ''}`} hint="Optional">
                  <input className={EW_INPUT} inputMode="decimal" aria-label="Cost per unit" value={f.unitCost} onChange={(e) => set({ unitCost: e.target.value })} />
                </EwField>
              )}
              <EwField label="Supplier" hint="Optional">
                <input className={EW_INPUT} aria-label="Supplier" value={f.supplier} onChange={(e) => set({ supplier: e.target.value })} />
              </EwField>
              <EwField label="Reference" hint="Delivery ticket / PO">
                <input className={EW_INPUT} aria-label="Reference" value={f.reference} onChange={(e) => set({ reference: e.target.value })} />
              </EwField>
            </>
          )}
          {action === 'adjust' && (
            <EwField label="Reason" hint="Required">
              <input className={EW_INPUT} aria-label="Reason" value={f.reason} onChange={(e) => set({ reason: e.target.value })} />
            </EwField>
          )}
        </div>
        <EwField label="Notes" hint="Optional">
          <input className={EW_INPUT} aria-label="Move notes" value={f.notes} onChange={(e) => set({ notes: e.target.value })} />
        </EwField>
        <div className="flex flex-wrap justify-end gap-2">
          <button type="button" className={EW_SECONDARY} onClick={onCancel} disabled={pending}>Cancel</button>
          <button type="submit" className={EW_PRIMARY} disabled={pending}>{pending ? 'Saving…' : title}</button>
        </div>
      </form>
    </EwPanel>
  );
}

export default function EwMaterialDetailScreen() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const openRecord = useOpenRecord();
  const back = useBackToList('/earthworks/inventory');
  const { user } = useAuth();
  const query = useEwMaterial(id);
  const meta = useEwMeta().data?.meta ?? null;
  const canEdit = canAdminEw(user?.role);
  const canMove = canDispatchEw(user?.role);
  const canSeePrices = canReadEwRates(user?.role);
  const locations = useEwMaterialLocations().data?.data ?? [];
  const update = useUpdateEwMaterial();
  const del = useDeleteEwMaterial();
  const [editing, setEditing] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [action, setAction] = useState<Action>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const m = query.data?.data ?? null;
  const options = query.data?.options;
  const failure = queryLoadFailure(query);
  const notFound = isNotFound(queryFailureError(query));
  const unit = m ? m.unitLabel || catalogLabel(options?.units, m.unit) : null;
  const moves = m?.moves ?? [];

  const moveColumns: EwColumn<EwMaterialMove>[] = [
    { key: 'day', header: 'Day', render: (v) => ewDay(v.movedOn) },
    { key: 'kind', header: 'Move', render: (v) => v.kindLabel || catalogLabel(options?.moveKinds, v.kind) },
    { key: 'qty', header: 'Quantity', align: 'right', render: (v) => <span className={`font-mono ${v.quantity < 0 ? 'text-fi-amber' : 'text-white'}`}>{ewSignedQty(v.quantity, unit)}</span> },
    { key: 'loc', header: 'Location', render: (v) => v.locationName || '—' },
    {
      key: 'what',
      header: 'Detail',
      render: (v) =>
        v.jobNumber ? (
          <button type="button" className="font-mono text-fieldiq-accent underline" onClick={(e) => { e.stopPropagation(); openRecord(`/earthworks/jobs/${v.jobId}?tab=logs`); }}>
            {v.jobNumber} · log {ewDay(v.logDate)}
          </button>
        ) : (
          [v.supplier, v.reference, v.reason].filter(Boolean).join(' · ') || '—'
        ),
    },
    ...(canSeePrices
      ? ([
          { key: 'cost', header: 'Cost', align: 'right', render: (v) => <span className="font-mono">{fmtMoney(v.totalCost, m?.currency)}</span> },
        ] as EwColumn<EwMaterialMove>[])
      : []),
    { key: 'by', header: 'By', render: (v) => v.createdByName || '—' },
  ];

  return (
    <div data-testid="ew-material-detail-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={m?.categoryLabel ? `${EW_EYEBROW} · Inventory · ${m.categoryLabel}` : `${EW_EYEBROW} · Inventory`}
        title={m?.name || (query.isLoading ? 'Item' : '—')}
        onBack={back}
        backLabel="Back"
        actions={
          m && canEdit ? (
            <div className="flex flex-wrap gap-2">
              <button type="button" className="fi-action fi-action--standard fi-action--primary" onClick={() => { setEditError(null); setEditing(true); }} disabled={editing}>
                <Pencil className="size-4" aria-hidden="true" />
                Edit item
              </button>
              <button type="button" className={DH_BAND_SECONDARY} onClick={() => { setDeleteError(null); setDeleting(true); }}>
                <Trash2 className="size-4" aria-hidden="true" />
                Delete
              </button>
            </div>
          ) : undefined
        }
        stats={[
          { icon: <Warehouse className="size-4" aria-hidden="true" />, label: 'On hand', tone: m && (m.lowStock || m.negative) ? 'accent' : 'neutral', loading: query.isLoading, value: m ? ewQty(m.onHand, unit) : '—' },
          { icon: <SlidersHorizontal className="size-4" aria-hidden="true" />, label: 'Reorder at', tone: 'neutral', loading: query.isLoading, value: m ? ewQty(m.lowStockThreshold, unit) : '—' },
          ...(canSeePrices
            ? [{ icon: <PackagePlus className="size-4" aria-hidden="true" />, label: 'Stock value', tone: 'neutral' as const, loading: query.isLoading, value: m ? fmtMoney(m.stockValue, m.currency) : '—' }]
            : []),
        ]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {query.isLoading ? (
            <EwLoading label="Loading item" />
          ) : notFound || (!failure && !m) ? (
            <EwPanel eyebrow="Inventory" title="Item">
              <EwEmpty>This item was not found. It may have been deleted.</EwEmpty>
            </EwPanel>
          ) : failure || !m ? (
            <EwPanel eyebrow="Inventory" title="Item">
              <LoadFailureNotice error={queryFailureError(query)} kind={failure ?? 'unreachable'} subject="this item" />
            </EwPanel>
          ) : (
            <>
              <EwStockWarnings messages={warnings.length ? warnings : m.stockWarning ? [m.stockWarning] : []} testId="ew-material-warning" />
              <EwPanel eyebrow="Inventory" title="Details" testId="ew-material-details">
                <dl className="grid gap-4 px-4 py-4 sm:grid-cols-2 lg:grid-cols-4">
                  <EwSpec label="Code" value={m.code || '—'} mono />
                  <EwSpec label="Category" value={m.categoryLabel || '—'} />
                  <EwSpec label="Unit" value={unit || '—'} />
                  <EwSpec label="Stock" value={<EwBadge skin={EW_STOCK_SKIN[stockState(m)]} value={stockState(m)} />} />
                  <EwSpec label="Home location" value={m.locationName || '—'} />
                  {canSeePrices && (
                    <>
                      <EwSpec label="Cost per unit" value={fmtMoney(m.costPerUnit, m.currency)} mono />
                      <EwSpec label="Sell price" value={fmtMoney(m.sellPrice, m.currency)} mono />
                      <EwSpec label="Rate-card material" value={m.rateMaterialLabel || '—'} />
                    </>
                  )}
                </dl>
                {m.notes && <p className="border-t border-fieldiq-border px-4 py-3 text-sm text-fieldiq-text">{m.notes}</p>}
              </EwPanel>

              {editing && canEdit && (
                <EwPanel eyebrow="Edit" title="Edit item" testId="ew-material-edit">
                  <EwMaterialForm
                    material={m}
                    options={options}
                    locations={locations}
                    currency={m.currency ?? null}
                    unitLocked={moves.length > 0}
                    pending={update.isPending}
                    error={editError}
                    submitLabel="Save item"
                    onCancel={() => setEditing(false)}
                    onSubmit={(body) =>
                      update.mutate({ id: m.id, body }, {
                        onSuccess: () => { toast.success('Item saved'); setEditing(false); },
                        onError: (err) => setEditError(ewRefusal(err, 'The item could not be saved.')),
                      })
                    }
                  />
                </EwPanel>
              )}

              <EwPanel
                eyebrow="Stock"
                title="On hand by location"
                testId="ew-material-by-location"
                actions={
                  canMove ? (
                    <div className="flex flex-wrap gap-2">
                      <button type="button" className={EW_PRIMARY} onClick={() => setAction('receive')}>
                        <PackagePlus className="size-4" aria-hidden="true" />
                        Stock in
                      </button>
                      <button type="button" className={EW_SECONDARY} onClick={() => setAction('adjust')}>
                        <SlidersHorizontal className="size-4" aria-hidden="true" />
                        Adjust
                      </button>
                      <button type="button" className={EW_SECONDARY} onClick={() => setAction('transfer')}>
                        <ArrowLeftRight className="size-4" aria-hidden="true" />
                        Transfer
                      </button>
                    </div>
                  ) : undefined
                }
              >
                {m.onHandByLocation.length === 0 ? (
                  <EwEmpty>Nothing on hand.</EwEmpty>
                ) : (
                  <ul className="divide-y divide-fieldiq-border">
                    {m.onHandByLocation.map((l) => (
                      <li key={l.locationId ?? 'none'} data-testid="ew-material-location-row" className="flex items-center justify-between px-4 py-3 text-sm">
                        <span className="text-fieldiq-text">{l.locationName || 'No location'}</span>
                        <span className={`font-mono ${l.onHand < 0 ? 'text-fi-amber' : 'text-white'}`}>{ewQty(l.onHand, unit)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </EwPanel>

              {action && canMove && (
                <MoveForm
                  key={action}
                  action={action}
                  material={m}
                  locations={locations}
                  today={meta?.today ?? null}
                  canSeePrices={canSeePrices}
                  onCancel={() => setAction(null)}
                  onDone={(res) => {
                    toast.success(action === 'receive' ? 'Stock in recorded' : action === 'adjust' ? 'Adjustment recorded' : 'Transfer recorded');
                    setWarnings(res.stockWarnings.map((w) => w.message));
                    setAction(null);
                  }}
                />
              )}

              <EwPanel eyebrow="History" title="Every move" testId="ew-material-history" footer={moves.length ? `${moves.length} move${moves.length === 1 ? '' : 's'}` : undefined}>
                <EwRegister
                  query={query}
                  rows={moves}
                  columns={moveColumns}
                  rail={(v) => (v.quantity < 0 ? 'border-l-fi-amber' : 'border-l-fi-green')}
                  subject="the history"
                  testId="ew-material-move"
                  minWidth="min-w-[900px]"
                  empty={<EwEmpty>No moves yet. Stock in is the first.</EwEmpty>}
                  card={(v) => (
                    <>
                      <div className="flex items-start justify-between gap-3">
                        <p className="text-sm font-semibold text-white">{v.kindLabel || v.kind} · {ewDay(v.movedOn)}</p>
                        <span className="font-mono text-sm text-white">{ewSignedQty(v.quantity, unit)}</span>
                      </div>
                      <p className="mt-1 text-xs text-fieldiq-muted">
                        {v.locationName || 'No location'} · {v.jobNumber ? `${v.jobNumber} · log ${ewDay(v.logDate)}` : [v.supplier, v.reference, v.reason].filter(Boolean).join(' · ') || '—'}
                      </p>
                    </>
                  )}
                />
              </EwPanel>
            </>
          )}
        </div>
      </section>

      {m && canEdit && (
        <EwDialog
          open={deleting}
          title="Delete item"
          onClose={() => setDeleting(false)}
          footer={
            <>
              <button type="button" className={EW_SECONDARY} onClick={() => setDeleting(false)} disabled={del.isPending}>Keep item</button>
              <button
                type="button"
                className={EW_PRIMARY}
                disabled={del.isPending}
                onClick={() =>
                  del.mutate(m.id, {
                    onSuccess: () => { toast.success('Item deleted'); navigate('/earthworks/inventory', { replace: true }); },
                    onError: (err) => setDeleteError(ewRefusal(err, 'The item could not be deleted.')),
                  })
                }
              >
                {del.isPending ? 'Deleting…' : 'Delete item'}
              </button>
            </>
          }
        >
          <EwFormError message={deleteError} />
          <p className="text-sm text-fieldiq-text">Removes {m.name} from the inventory. Its history is kept. An item with stock anywhere cannot be deleted.</p>
        </EwDialog>
      )}
    </div>
  );
}
